import { useState } from "react";
import { Drawer, DrawerContent, DrawerTitle } from "./ui/drawer";
import { CaretDown, Check } from "@phosphor-icons/react";

const Trigger = ({ label, placeholder, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="w-full h-12 px-4 rounded-xl bg-background border border-border flex items-center justify-between text-left"
  >
    <span className={label ? "text-foreground" : "text-muted-foreground"}>{label || placeholder}</span>
    <CaretDown size={16} className="text-muted-foreground shrink-0" />
  </button>
);

export const SheetPicker = ({ title, options, value, onChange, placeholder = "Select", testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  return (
    <>
      <Trigger label={selected?.label} placeholder={placeholder} onClick={() => setOpen(true)} testId={testId} />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl bg-white">
          <DrawerTitle className="px-5 pt-2 pb-3 text-base font-heading font-semibold">{title}</DrawerTitle>
          <div className="max-h-[60vh] overflow-y-auto px-2 pb-6">
            {options.length === 0 && (
              <p className="px-4 py-6 text-sm text-muted-foreground">Nothing here yet.</p>
            )}
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                data-testid={`picker-option-${o.id}`}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className={`w-full min-h-[48px] px-4 py-2 rounded-xl flex items-center justify-between text-left active:bg-secondary ${
                  o.id === value ? "bg-secondary" : ""
                }`}
              >
                <span>
                  <span className="block text-sm font-medium">{o.label}</span>
                  {o.sub && <span className="block text-xs text-muted-foreground">{o.sub}</span>}
                </span>
                {o.id === value && <Check size={18} />}
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const MultiSheetPicker = ({ title, options, values = [], onChange, placeholder = "Select", testId }) => {
  const [open, setOpen] = useState(false);
  const label = values.length ? options.filter((o) => values.includes(o.id)).map((o) => o.label).join(", ") : "";
  const toggle = (id) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
  return (
    <>
      <Trigger label={label} placeholder={placeholder} onClick={() => setOpen(true)} testId={testId} />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl bg-white">
          <DrawerTitle className="px-5 pt-2 pb-3 text-base font-heading font-semibold">{title}</DrawerTitle>
          <div className="max-h-[55vh] overflow-y-auto px-2">
            {options.length === 0 && (
              <p className="px-4 py-6 text-sm text-muted-foreground">Nothing here yet.</p>
            )}
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                data-testid={`picker-multi-${o.id}`}
                onClick={() => toggle(o.id)}
                className={`w-full min-h-[48px] px-4 py-2 rounded-xl flex items-center justify-between text-left active:bg-secondary ${
                  values.includes(o.id) ? "bg-secondary" : ""
                }`}
              >
                <span className="text-sm font-medium">{o.label}</span>
                {values.includes(o.id) && <Check size={18} />}
              </button>
            ))}
          </div>
          <div className="p-4 pb-6">
            <button
              type="button"
              data-testid="picker-multi-done"
              onClick={() => setOpen(false)}
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform"
            >
              Done
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
